/* eslint-disable no-shadow */
import { createSelector } from '@reduxjs/toolkit/';
import { memoize } from 'lodash-es';

import {
  distance,
  resultIsPlaceType,
  getTierDesignationContent,
  formatPlacePhoneNumber,
} from 'utils/utils';
import { PLACES_SUBSPECIALTY_SECTION } from 'utils/constants';
import * as selectResults from './selectResults';
import * as selectClient from '../config/selectClient';
import * as selectContent from '../config/selectContent';
import * as selectFeatureFlags from '../config/selectFeatureFlags';
import * as selectNetworks from '../config/selectNetworks';
import { getPlaceNetworkContent, normalizeHOR, formatBusinessHours } from './placeUtils';
import { getNetworkHealthSystemTier, getHealthSystemTierPillProps } from './resultUtils';
import fusionApi from '../../query/fusion/fusionApi';

/* ************************************************ */
/* **** Initial selectPlaceById Function ******* */
/* ************************************************ */

/** This function selects a result by id from the results object. It verifies that the given result exists, AND is a place object.
 *  @param {string} id This can be a placeId OR a queryCacheKey from places fetched using RTK Query
 *  @returns {Function} A selector function
 * */
const placeById = memoize((id) => {
  if (id?.startsWith(fusionApi.endpoints.getPlace.name)) {
    // if a RTK queryCacheKey is passed it will look like this: 'getPlace({"arg1":1,"arg2":"b"})'
    let cacheKey = id;
    cacheKey = cacheKey.replace(fusionApi.endpoints.getPlace.name, ''); // remove getPlace leaving '({"arg1":1,"arg2":"b"})'
    cacheKey = cacheKey.slice(1, -1); // removes leading and trailing ( ) leaving '{"arg1":1,"arg2":"b"}'

    try {
      cacheKey = JSON.parse(cacheKey);
    } catch (e) {
      console.error(e);
      return () => undefined;
    }
    const rtkQuerySelector = fusionApi.endpoints.getPlace.select(cacheKey); // (state) => state.fusion.queries['getPlace(.....)']

    // now return our own selector that specifically returns the place data
    return (state) => rtkQuerySelector(state)?.data;
  }

  // regular place id was passed
  return createSelector([selectResults.all], (resultObject) => {
    const place = resultObject[id];
    if (!place) return undefined;

    if (!resultIsPlaceType(resultObject[id])) {
      return undefined;
    }
    return place;
  });
});

/* ************************************************ */
/* *********** Computed Properties **************** */
/* ************************************************ */

/**
 * Returns the phone from the place
 * @returns {Function} */
function phoneNumber(id) {
  return createSelector([placeById(id)], (place) => {
    if (!place?.phone) return null;
    return formatPlacePhoneNumber(place.phone);
  });
}

/**
 * Returns the address from the place
 * @returns {Function} */
function address(id) {
  return createSelector([placeById(id)], (place) => {
    if (!place) return null;

    // generate the first part of the string, combining address1 & address2 based on extraLineBreaks prop
    let addressDisplayString = place.address1;

    // 111 Main St, Ste 123
    if (place.address2) addressDisplayString += `, ${place.address2}`;
    addressDisplayString += ` \n${place.city}, ${place.state} ${place.zipcode}`;

    return addressDisplayString;
  });
}

/**
 * Returns the distance between the place, and the coordinates of the last search (from resultsSlice) in miles
 * @returns {Function} */
function distanceInMiles(id) {
  return createSelector([placeById(id), selectResults.coordinates], (place, locationSearched) => {
    if (!place) return null;
    if (!locationSearched.latitude || !locationSearched.longitude) return null; // can't determine a distance if we don't have a location to compare to

    // // if distance has not been calculated by the backend, calculate it here
    return distance(
      locationSearched.latitude,
      locationSearched.longitude,
      place.latitude,
      place.longitude
    );
  });
}

/** @returns {Function} */
function placeFocusAreas(id) {
  return createSelector(
    [placeById(id), selectResults.classificationId],
    (place, searchedClassificationId = null) => {
      if (!place) return [];
      const { classification } = place;
      if (!Array.isArray(classification.focusAreas)) return [];

      const copiedFocusAreas = [...classification.focusAreas];
      return copiedFocusAreas.sort((a, b) => {
        if (searchedClassificationId) {
          // sort the matching focusArea to the front of the metrics list if we have a searched classification id
          if (searchedClassificationId === a.emboldId) return -1;
          if (searchedClassificationId === b.emboldId) return 1;
        }
        return a.name.localeCompare(b.name);
      });
    }
  );
}

/** @returns {Function} */
function blueCareDistinctions(id) {
  return createSelector([placeById(id)], (place) => {
    if (!place) return [];
    return place?.extension?.blueCareDistinction || [];
  });
}

/** @returns {Function} */
function placeTypes(id) {
  return createSelector(
    [placeById(id), selectResults.classificationId],
    (place, searchedClassificationId = null) => {
      if (!place) return [];
      const { classification } = place;
      if (!Array.isArray(classification.types)) return [];

      const copiedTypes = [...classification.types];
      return copiedTypes.sort((a, b) => {
        if (searchedClassificationId) {
          // sort the matching types to the front of the metrics list if we have a searched classification id
          if (searchedClassificationId === a.emboldId) return -1;
          if (searchedClassificationId === b.emboldId) return 1;
        }
        return a.name.localeCompare(b.name);
      });
    }
  );
}

/** @returns {Function} */
function tierDesignation(id) {
  return createSelector([placeById(id), selectNetworks.currentSlug], (place, networkSlug) => {
    if (!place) return null;
    if (!place.tiering) return null;

    const tier = place.tiering[networkSlug];

    if (!tier) return null;
    return getTierDesignationContent(tier);
  });
}

function hoursOfOperation(id) {
  return createSelector([placeById(id)], (place) => {
    if (!place) return null;
    if (!place.hoursOfOperation) return null;

    const normalizedHOR = normalizeHOR(place.hoursOfOperation);
    const dateToday = new Date().getDay();
    const today = normalizedHOR.find((day) => day.order === dateToday);

    return {
      currentDay: today,
      schedule: normalizedHOR,
    };
  });
}

/**
 * @param {string} id Place id
 * @returns {string} A string representation of the place object used for copy and paste feature
 */
export function getPlaceString(id) {
  return createSelector(
    [placeById(id), distanceInMiles(id), hoursOfOperation(id), selectResults.wasPlaceSearch],
    (place, distanceInMiles, hoursOfOperation, wasPlaceSearch) => {
      if (!wasPlaceSearch) return null;
      const NA = 'N/A'; // constant used when no value is available

      const distance = distanceInMiles || NA;

      let result = ''; // complete string to be returned at the end

      const appendLine = (text) => {
        result += `\n${text}`;
      };
      const appendHeader = (text) => {
        appendLine(`--${text}--`);
      };
      // name
      result = `Name: ${place.name}`;

      // OVERVIEW SECTION
      appendHeader('Overview');

      // get the text used for Network Coverage. This function comes from the <CoverageRibbon />
      appendLine('Network Coverage: In Network');

      appendLine(`Phone Number: ${place.phone}`);

      appendLine(`Distance: ${distance} miles`);
      appendLine(`Hospital Safety Grade: ${place.hospitalSafetyGrade || NA}`);
      appendLine(`Address: ${place.address1}`);
      appendLine(`Handicap Accessible: ${place.isWheelchairAccessible || NA}`);
      appendLine(`NPI: ${place.npi}`);
      appendLine(`--Hours of Operation-- \n${formatBusinessHours(hoursOfOperation?.schedule)}`);

      // specialty areas
      if (place?.classification?.focusAreas?.length) {
        let focusAreasString = '';
        for (let i = 0; i < place.classification.focusAreas.length; i += 1) {
          focusAreasString += place.classification.focusAreas[i].name;
          if (i < place.classification.focusAreas.length - 1) focusAreasString += ', ';
        }

        appendLine(`${PLACES_SUBSPECIALTY_SECTION.title}: ${focusAreasString}`);
      }

      return result;
    }
  );
}

export function getPlacesString(ids) {
  const placeStrings = ids.map((id) => getPlaceString(id));
  return createSelector(
    [selectResults.wasPlaceSearch, ...placeStrings],
    (wasPlaceSearch, ...placeStrings) => {
      if (!wasPlaceSearch) return null;
      let result = '';
      if (placeStrings?.length) result += placeStrings.join('\n\n');
      return result;
    }
  );
}

/** @returns {Function} */
function networkContent() {
  return createSelector([selectClient.name], (client) => getPlaceNetworkContent(client));
}

function isClientFeatured(id) {
  return createSelector(
    [placeById(id), selectFeatureFlags.showClientFeatured, selectContent.featuredPlaceText],
    (place, showClientFeatured, featuredPlaceText) => {
      if (!place) return null;
      if (!showClientFeatured) return false; // cannot be featured if the feature is disabled
      if (!featuredPlaceText) return false; // cannot be featured if there is no global text to show

      return Boolean(place.clientFeatured);
    }
  );
}

function clientFeaturedPill(id) {
  return createSelector(
    [isClientFeatured(id), selectContent.featuredPlaceText],
    (isClientFeatured, clientFeaturedContent) => {
      if (!isClientFeatured) return null;
      if (!clientFeaturedContent) return null;

      const { banner, tooltip, color, icon, link } = clientFeaturedContent;
      const props = {
        label: banner,
        color,
        iconUrl: icon,
        TooltipProps: {
          message: tooltip,
          ...(link ? { href: { to: link, label: 'More Info Here' } } : {}),
        },
      };

      return props;
    }
  );
}

function clientFeaturedBadge(id) {
  return clientFeaturedPill(id); // badge uses all of the same props as the pill
}

function networkHealthSystemTier(id) {
  return createSelector([placeById(id), selectNetworks.currentSlug], getNetworkHealthSystemTier);
}

function healthSystemTierPill(id) {
  return createSelector(
    [selectClient.name, networkHealthSystemTier(id)],
    getHealthSystemTierPillProps
  );
}

/** Based on healthSystemTier pill */
export function healthSystemTierBadge(id) {
  return createSelector([healthSystemTierPill(id)], (pillProps) => pillProps);
}

export function providersAtThisLocationUrl(id) {
  return createSelector([selectResults.profileDetailsLink(id)], (profileUrl) => {
    if (!profileUrl) return '';

    const [path, qs = ''] = profileUrl.split('?');

    let newPath = path;
    if (path.endsWith('/')) newPath += 'providers-at';
    else newPath += '/providers-at';

    if (!qs) return newPath;
    return [newPath, qs].join('?');
  });
}

export function hospitalSafetyGradeContent(id) {
  return createSelector([placeById(id)], (place) => {
    if (!place) return {};

    const { name, hospitalGrade, hospitalGradeLastUpdated, hospitalGradeUrl } = place;
    if (!hospitalGrade) return {};

    return {
      hospitalGradeLastUpdated,
      hospitalGrade,
      title: `Hospital Safety Grade: ${hospitalGrade}`,
      ariaLabel: `${name} Hospital Safety Grade`,
      TooltipProps: {
        title: `Hospital Safety Grade: ${hospitalGrade}`,
        message:
          'The Hospital Safety Grade assigns an ‘A’, ‘B’, ‘C’, ‘D’ or ‘F’ grade to nearly 3,000 hospitals across the country based on their performance in preventing medical errors, injuries, accidents, infections and other harms to patients in their care.',
        ...(hospitalGradeUrl ? { href: { to: hospitalGradeUrl, label: 'Learn More' } } : {}),
      },
    };
  });
}

/* ************************************************ */
/* *************** Master Function **************** */
/* ************************************************ */

/**
 * This master selectPlace function accepts an id string and builds selectors for that given id.
 * Every selector needs to be passed into useSelector or given the argument of state to return a value.
 * @param {string} id
 *
 * @example
 * // in a component
 * const computedProperty = useSelector(selectPlace('abc').computedProperty);
 *
 * // in a thunk
 * const state = getState();
 * const computedProperty = selectPlace('123').computedProperty(state);
 */
function selectPlace(id) {
  // return an object of selectors
  return {
    data: placeById(id),
    distanceInMiles: distanceInMiles(id),
    phoneNumber: phoneNumber(id),
    address: address(id),
    tierDesignation: tierDesignation(id),
    hoursOfOperation: hoursOfOperation(id),
    networkContent: networkContent(),
    isClientFeatured: isClientFeatured(id),
    clientFeaturedPill: clientFeaturedPill(id),
    clientFeaturedBadge: clientFeaturedBadge(id),
    healthSystemTierPill: healthSystemTierPill(id),
    healthSystemTierBadge: healthSystemTierBadge(id),
    placeFocusAreas: placeFocusAreas(id),
    placeTypes: placeTypes(id),
    providersAtThisLocationUrl: providersAtThisLocationUrl(id),
    hospitalSafetyGradeContent: hospitalSafetyGradeContent(id),
    blueCareDistinctions: blueCareDistinctions(id),
  };
}

// this ensures that when we call select.place('abc'), it generates n number of selectors for us. But calling select.place('abc') again does not rebuild the exact same n number of selectors
export default memoize(selectPlace);
